package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.FilterResultsUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel

class FilterResultsBottomSheetViewModel(
    context: AppContext,
    private val onClose: () -> Unit,
    private val onApply: (FilterResultsUIState) -> Unit = {},
    initialFilter: FilterResultsUIState? = null,
    /** Merchant locations (id to display name) available in current results for multi-select filter. */
    val availableLocations: List<Pair<Long, String>> = emptyList()
) : ChildViewModel<FilterResultsUIState>(
    context,
    initialFilter ?: FilterResultsUIState()
) {

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.filter)
    }

    override fun onBackButtonClicked() {
        onClose()
    }

    fun onBackClick() = onClose()

    fun onApplyClick() {
        onApply(uiState.value)
        onClose()
    }

    fun onResetFiltersClick() {
        updateState {
            FilterResultsUIState()
        }
    }

    fun setUseMyLocation(use: Boolean) {
        updateState { it.copy(useMyLocation = use) }
    }

    fun setDistanceKm(km: Float) {
        updateState { it.copy(distanceKm = km) }
    }

    fun setSelectedLocationId(locationId: Long?) {
        updateState { it.copy(selectedLocationId = locationId) }
    }

    fun setPriceMin(value: String) {
        updateState { it.copy(priceMin = sanitizePriceInput(value)) }
    }

    fun setPriceMax(value: String) {
        updateState { it.copy(priceMax = sanitizePriceInput(value)) }
    }

    /**
     * Same rules as the regular price field when adding a bag (AddBagViewModel.filterDecimalInput):
     * - Decimal separator: comma or dot (normalized to dot for storage)
     * - Thousands separator: space or non-breaking space (stripped)
     * - At most 2 decimal places (EUR cents)
     * - No forced padding while typing (user can enter "20", "20.5", "20.50")
     * - Leading zeros in integer part removed (012.50 → 12.50)
     * - Max value 999.99 is enforced in the UI (input rejected + snackbar)
     */
    fun sanitizePriceInput(input: String): String {
        if (input.isEmpty()) return ""
        val noSpaces = input.replace(" ", "").replace("\u00A0", "")
        val normalized = noSpaces.replace(',', '.')
        val filtered = normalized.filter { it.isDigit() || it == '.' }
        val parts = filtered.split('.')
        return when {
            parts.size == 1 -> {
                val integerPart = parts[0]
                if (integerPart.isEmpty()) ""
                else integerPart.trimStart('0').ifEmpty { "0" }
            }
            else -> {
                val integerPart = parts[0]
                val decimalPart = parts[1].take(2)
                when {
                    integerPart.isEmpty() && decimalPart.isEmpty() -> ""
                    integerPart.isEmpty() -> "0.$decimalPart"
                    else -> {
                        val cleanedInteger = integerPart.trimStart('0').ifEmpty { "0" }
                        "$cleanedInteger.$decimalPart"
                    }
                }
            }
        }
    }

    fun showPriceMaxExceededMessage() {
        showSnackbar(sharedString(SR.strings.filter_price_max_999))
    }

    fun setSelectedRatingIndex(index: Int) {
        updateState { it.copy(selectedRatingIndex = index) }
    }

    fun setSelectedPublishedDateIndex(index: Int) {
        updateState { it.copy(selectedPublishedDateIndex = index) }
    }

    fun setSelectedCollectionTimeIndex(index: Int) {
        updateState { it.copy(selectedCollectionTimeIndex = index) }
    }
}
